import { globalRef, type Unsubscribe } from '@amazon/vinyl-util'

/** This install's device, as the desktop shell registers it with Panda. */
export interface DesktopDevice {
    readonly deviceId: string
    readonly deviceType: string
    readonly deviceName: string
}

/** A desktop session: the marketplace's amazon TLD and its music territory. */
export interface DesktopSession {
    readonly marketplace: string
    readonly territory: string
}

export type DesktopSignInOutcome =
    | { readonly outcome: 'signedIn'; readonly session: DesktopSession }
    | { readonly outcome: 'canceled' }
    | { readonly outcome: 'wrongMarketplace'; readonly marketplace: string }

/** Where the sign-in shows inside the main window, in physical pixels of the page. */
export interface SignInBounds {
    readonly x: number
    readonly y: number
    readonly width: number
    readonly height: number
}

/** A sign-in embedded in the main window, and whether the page is dark (Auth Portal's skin). */
export interface SignInEmbed {
    readonly bounds: SignInBounds
    readonly dark: boolean
}

/** What `desktop_auth_token` rejects with once the session is gone. */
export const DESKTOP_SIGNED_OUT = 'signedOut'

/**
 * The desktop shell's sign-in (`packages/bf-tauri/src-tauri/src/desktop_auth.rs`). The shell
 * keeps the device's refresh token; the page only ever sees access tokens.
 */
export interface DesktopShell {
    device(): Promise<DesktopDevice>
    /** The saved session after the shell has tried its refresh token; null when signed out. */
    restore(): Promise<DesktopSession | null>
    /**
     * Signs in on `marketplace` (an amazon TLD) inside the main window with `embed`, else in a
     * window of its own; `verifyHome` checks the account's home market.
     */
    signIn(
        marketplace: string,
        verifyHome: boolean,
        embed: SignInEmbed | null
    ): Promise<DesktopSignInOutcome>
    /** Moves the embedded sign-in; rejects on a native build that can only open a window. */
    placeSignIn(bounds: SignInBounds): Promise<void>
    /** Whether the embedded sign-in shows Auth Portal's sign-in page, as it navigates. */
    onSignInPage(listener: (onSignInPage: boolean) => void): Promise<Unsubscribe>
    /** Closes the open sign-in, ending `signIn` as canceled; a no-op with none open. */
    cancelSignIn(): Promise<void>
    token(): Promise<string>
    signOut(): Promise<void>
}

const SIGN_IN_PAGE_EVENT = 'desktop-auth-sign-in-page'

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
    const tauri = await import('@tauri-apps/api/core')
    return tauri.invoke<T>(command, args)
}

/** The Tauri commands behind {@link DesktopShell}. */
export const tauriDesktopShell: DesktopShell = {
    device: () => invoke('desktop_auth_device'),
    restore: () => invoke('desktop_auth_restore'),
    signIn: (marketplace, verifyHome, embed) =>
        invoke('desktop_auth_sign_in', { marketplace, verifyHome, embed }),
    placeSignIn: (bounds) => invoke('desktop_auth_place_sign_in', { bounds }),
    onSignInPage: async (listener) => {
        const { listen } = await import('@tauri-apps/api/event')
        return listen<boolean>(SIGN_IN_PAGE_EVENT, (event) => listener(event.payload))
    },
    cancelSignIn: () => invoke('desktop_auth_cancel_sign_in'),
    token: () => invoke('desktop_auth_token'),
    signOut: () => invoke('desktop_auth_sign_out'),
}

/** The shell the desktop sign-in talks to; tests set a fake. */
export const desktopShellRef = globalRef<DesktopShell>(() => tauriDesktopShell)
